import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';  
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { useSelector } from 'react-redux';
import { Button } from '@heroui/react';
import { faFileExcel, faFilePdf, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MainContainer } from '../../components';
import { autoTable } from "jspdf-autotable"

function GstPeriodicSummary() {
    const location = useLocation();
    const { reportData, fromDate, toDate } = location.state || {};
    const { compName, gstNo } = useSelector((state) => state.company);
    const navigate = useNavigate();

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '/');
    };

    // Format number with 2 decimal places
    const formatCurrency = (num) => {
        if (num === null || num === undefined) return '0.00';
        return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Process data for the table
    const inwardSupplies = reportData?.[0]?.filter(item => item.Book === 'Purchase') || [];
    const outwardSupplies = reportData?.[0]?.filter(item => item.Book === 'Sales') || [];

    // Calculate totals
    const inwardTotals = inwardSupplies.reduce((acc, item) => ({
        NetAmount: acc.NetAmount + (item.NetAmount || 0),
        TaxableValue: acc.TaxableValue + (item.TaxableValue || 0),
        CGST: acc.CGST + (item.CGST || 0),
        SGST: acc.SGST + (item.SGST || 0),
        IGST: acc.IGST + (item.IGST || 0),
        CESS: acc.CESS + (item.CESS || 0),
        TotalTax: acc.TotalTax + (item.TotalTax || 0)
    }), { NetAmount: 0, TaxableValue: 0, CGST: 0, SGST: 0, IGST: 0, CESS: 0, TotalTax: 0 });

    const outwardTotals = outwardSupplies.reduce((acc, item) => ({
        NetAmount: acc.NetAmount + (item.NetAmount || 0),
        TaxableValue: acc.TaxableValue + (item.TaxableValue || 0),
        CGST: acc.CGST + (item.CGST || 0),
        SGST: acc.SGST + (item.SGST || 0),
        IGST: acc.IGST + (item.IGST || 0),
        CESS: acc.CESS + (item.CESS || 0),
        TotalTax: acc.TotalTax + (item.TotalTax || 0)
    }), { NetAmount: 0, TaxableValue: 0, CGST: 0, SGST: 0, IGST: 0, CESS: 0, TotalTax: 0 });

    const totalGstCredit = inwardTotals.CGST + inwardTotals.SGST + inwardTotals.IGST;
    const totalGstDebit = outwardTotals.CGST + outwardTotals.SGST + outwardTotals.IGST;
    const diffToPay = totalGstDebit - totalGstCredit;

    const exportToPDF = () => {
        const doc = new jsPDF({
            orientation: 'Landscape',
            unit: 'mm'
        });

        // Set font
        doc.setFont('helvetica', 'normal');

        // Prepare data for the table
        const headers = [
            [
                'Book',
                'Particulars',
                'Net Amount',
                'Taxable Value',
                'CGST',
                'SGST',
                'IGST',
                'CESS',
                'Total Tax'
            ]
        ];

        const body = [
            // Inward supplies header
            [{
                content: 'INWARD SUPPLIES (PURCHASE)',
                colSpan: 10,
                styles: {
                    fillColor: [230, 247, 230],
                    textColor: [0, 100, 0],
                    fontStyle: 'bold',
                    halign: 'center'
                }
            }],
            // Inward supplies data
            ...inwardSupplies.map(item => [
                item.Book,
                item.Particulars,
                formatCurrency(item.NetAmount),
                formatCurrency(item.TaxableValue),
                formatCurrency(item.CGST),
                formatCurrency(item.SGST),
                formatCurrency(item.IGST),
                formatCurrency(item.CESS),
                formatCurrency(item.TotalTax)
            ]),
            // Inward totals
            [{
                content: 'TOTAL INWARD',
                colSpan: 3,
                styles: { fontStyle: 'bold', halign: 'left' }
            },
            formatCurrency(inwardTotals.NetAmount),
            formatCurrency(inwardTotals.TaxableValue),
            formatCurrency(inwardTotals.CGST),
            formatCurrency(inwardTotals.SGST),
            formatCurrency(inwardTotals.IGST),
            formatCurrency(inwardTotals.CESS),
            formatCurrency(inwardTotals.TotalTax)],

            // Empty row
            ['', '', '', '', '', '', '', '', '', ''],

            // Outward supplies header
            [{
                content: 'OUTWARD SUPPLIES (SALES)',
                colSpan: 10,
                styles: {
                    fillColor: [255, 230, 230],
                    textColor: [139, 0, 0],
                    fontStyle: 'bold',
                    halign: 'center'
                }
            }],
            // Outward supplies data
            ...outwardSupplies.map(item => [
                item.Book,
                item.Particulars,
                formatCurrency(item.NetAmount),
                formatCurrency(item.TaxableValue),
                formatCurrency(item.CGST),
                formatCurrency(item.SGST),
                formatCurrency(item.IGST),
                formatCurrency(item.CESS),
                formatCurrency(item.TotalTax)
            ]),
            // Outward totals
            [{
                content: 'TOTAL OUTWARD',
                colSpan: 3,
                styles: { fontStyle: 'bold', halign: 'left' }
            },
            formatCurrency(outwardTotals.NetAmount),
            formatCurrency(outwardTotals.TaxableValue),
            formatCurrency(outwardTotals.CGST),
            formatCurrency(outwardTotals.SGST),
            formatCurrency(outwardTotals.IGST),
            formatCurrency(outwardTotals.CESS),
            formatCurrency(outwardTotals.TotalTax)],

            // Empty row
            ['', '', '', '', '', '', '', '', '', ''],

            // GST Summary
            [{
                content: 'GST SUMMARY',
                colSpan: 10,
                styles: {
                    fillColor: [230, 230, 255],
                    textColor: [0, 0, 139],
                    fontStyle: 'bold',
                    halign: 'center'
                }
            }],
            // GST Credit Total
            [{
                content: 'TOTAL GST CREDIT (INWARD)',
                colSpan: 2,
                styles: { fontStyle: 'bold', halign: 'left' }
            },
                '',
                '',
                '',
                '',
                '',
                '',
            formatCurrency(totalGstCredit)],

            // GST Debit Total row
            [{
                content: 'TOTAL GST DEBIT (OUTWARD)',
                colSpan: 2,
                styles: { fontStyle: 'bold', halign: 'left' }
            },
                '',
                '',
                '',
                '',
                '',
                '',
            formatCurrency(totalGstDebit)],

            // Difference to Pay row
            [{
                content: 'DIFF. (TO PAY)',
                colSpan: 2,
                styles: { fontStyle: 'bold', halign: 'left' }
            },
                '',
                '',
                '',
                '',
                '',
                '',
            {
                content: formatCurrency(diffToPay),
                styles: {
                    fontStyle: 'bold',
                    textColor: diffToPay >= 0 ? [0, 100, 0] : [139, 0, 0]
                }
            }]
        ];

        // Column styles
        const columnStyles = {
            0: { cellWidth: 25, halign: 'center' },  // Book
            1: { cellWidth: 80, halign: 'left' },    // Particulars
            2: { cellWidth: 30, halign: 'right' },   // Net Amount
            3: { cellWidth: 30, halign: 'right' },   // Taxable Value
            4: { cellWidth: 20, halign: 'right' },   // CGST
            5: { cellWidth: 20, halign: 'right' },   // SGST
            6: { cellWidth: 20, halign: 'right' },   // IGST
            7: { cellWidth: 20, halign: 'right' },   // CESS
            8: { cellWidth: 30, halign: 'right' }    // Total Tax
        };

        // Generate PDF
        autoTable(doc,{
            startY: 30,
            margin: { top: 25, left: 10, right: 10, bottom: 15 },
            head: headers,
            body: body,
            theme: 'grid',
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontStyle: 'bold',
                halign: 'center'
            },
            columnStyles: columnStyles,
            styles: {
                fontSize: 8,
                cellPadding: 2,
                overflow: 'linebreak',
                valign: 'middle'
            },
            didDrawPage: function (data) {
                // Header
                doc.setFontSize(10);
                doc.setFont('helvetica', 'bold');
                doc.text(compName, 14, 10);
                doc.text(`GST No: ${gstNo}`, 14, 15);
                doc.text('Report: GST Summary Periodwise', 14, 20);
                doc.text(`From: ${formatDate(fromDate)} To: ${formatDate(toDate)}`,
                    doc.internal.pageSize.width - 14, 20, { align: 'right' });

                // Footer
                doc.setFontSize(8);
                doc.setTextColor(150);
                doc.text(
                    `Page ${data.pageNumber}`,
                    doc.internal.pageSize.width / 2,
                    doc.internal.pageSize.height - 10,
                    { align: 'center' }
                );
            }
        });

        doc.save('GST_Periodic_Summary.pdf');
    };

    // Export to Excel
    const exportToExcel = () => {
        // Prepare worksheet data
        const wsData = [
            ['Book', 'Particulars', 'Net Amount', 'Taxable Value', 'CGST', 'SGST', 'IGST', 'CESS', 'Total Tax'],
            ['INWARD SUPPLIES (PURCHASE)', '', '', '', '', '', '', '', '', ''],
            ...inwardSupplies.map(item => [
                item.Book,
                item.Particulars,
                item.NetAmount,
                item.TaxableValue,
                item.CGST,
                item.SGST,
                item.IGST,
                item.CESS,
                item.TotalTax
            ]),
            ['TOTAL INWARD', '', '', inwardTotals.NetAmount, inwardTotals.TaxableValue, inwardTotals.CGST, inwardTotals.SGST, inwardTotals.IGST, inwardTotals.CESS, inwardTotals.TotalTax],
            ['', '', '', '', '', '', '', '', '', ''],
            ['OUTWARD SUPPLIES (SALES)', '', '', '', '', '', '', '', '', ''],
            ...outwardSupplies.map(item => [
                item.Book,
                item.Particulars,
                item.NetAmount,
                item.TaxableValue,
                item.CGST,
                item.SGST,
                item.IGST,
                item.CESS,
                item.TotalTax
            ]),
            ['TOTAL OUTWARD', '', '', outwardTotals.NetAmount, outwardTotals.TaxableValue, outwardTotals.CGST, outwardTotals.SGST, outwardTotals.IGST, outwardTotals.CESS, outwardTotals.TotalTax],
            ['', '', '', '', '', '', '', '', '', ''],
            ['GST SUMMARY', '', '', '', '', '', '', '', '', ''],
            ['TOTAL GST CREDIT (INWARD)', '', '', '', '', inwardTotals.CGST, inwardTotals.SGST, inwardTotals.IGST, '', totalGstCredit],
            ['TOTAL GST DEBIT (OUTWARD)', '', '', '', '', outwardTotals.CGST, outwardTotals.SGST, outwardTotals.IGST, '', totalGstDebit],
            ['DIFF. (TO PAY)', '', '', '', '', '', '', '', '', diffToPay]
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "GST Summary");

        // Auto-size columns
        const wscols = [
            { wch: 15 }, // Book
            { wch: 50 }, // Particulars
            { wch: 15 }, // Net Amount
            { wch: 15 }, // Taxable Value
            { wch: 10 }, // CGST
            { wch: 10 }, // SGST
            { wch: 10 }, // IGST
            { wch: 10 }, // CESS
            { wch: 15 }  // Total Tax
        ];
        ws['!cols'] = wscols;

        // Format totals rows
        const formatTotals = (row) => {
            for (let i = 0; i < 10; i++) {
                const cellRef = XLSX.utils.encode_cell({ r: row, c: i });
                if (!ws[cellRef]) ws[cellRef] = {};
                ws[cellRef].s = { font: { bold: true } };
                if (i >= 3) {
                    ws[cellRef].t = 'n';
                    ws[cellRef].z = '#,##0.00';
                }
            }
        };

        // Format inward totals (row 2 + inwardSupplies.length + 1)
        formatTotals(inwardSupplies.length + 2);
        // Format outward totals (row 5 + inwardSupplies.length + outwardSupplies.length + 2)
        formatTotals(inwardSupplies.length + outwardSupplies.length + 5);
        // Format GST summary rows
        [inwardSupplies.length + outwardSupplies.length + 8,
        inwardSupplies.length + outwardSupplies.length + 9,
        inwardSupplies.length + outwardSupplies.length + 10].forEach(row => {
            formatTotals(row);
        });

        XLSX.writeFile(wb, 'GST_Periodic_Summary.xlsx');
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    return (
         <MainContainer
              pageName="GST Periodic Summary Report"
              breadcrumbs={["Reports", "GST Reports", "GST Periodic Summary"]}
            >
        <div className="min-h-screen bg-gray-50 p-0">
            <div className="max-w-full mx-auto">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h1 className="text-md font-semibold text-lime-800">GST Summary Periodwise Report</h1>
                            <p className="text-sm text-pink-600">
                                {fromDate && toDate ? (
                                    `From ${formatDate(fromDate)} To ${formatDate(toDate)}`
                                ) : 'Date range not specified'}
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                onPress={exportToPDF}
                                size="sm"
                                color="danger"
                                startContent={<FontAwesomeIcon icon={faFilePdf} className="h-4 w-4" />}
                            >
                                PDF
                            </Button>
                            <Button
                                onPress={exportToExcel}
                                size="sm"
                                color="success"
                                startContent={<FontAwesomeIcon icon={faFileExcel} className="h-4 w-4" />}
                            >
                                Excel
                            </Button>
                            <Button
                                onPress={handleGoBack}
                                size="sm"
                                variant="bordered"
                                color='primary'
                                startContent={<FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />}
                            >
                                Back
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Table Container */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Book</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Particulars</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Net Amount</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Taxable Value</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CGST</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">SGST</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">IGST</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CESS</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Tax</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {/* Inward Supplies */}
                                <tr className="bg-green-50">
                                    <td colSpan="9" className="px-4 py-2 font-medium text-green-800">INWARD SUPPLIES (PURCHASE)</td>
                                </tr>

                                {inwardSupplies.map((item, index) => (
                                    <tr key={`inward-${index}`} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{item.Book}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500">{item.Particulars}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.NetAmount)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.TaxableValue)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.CGST)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.SGST)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.IGST)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.CESS)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.TotalTax)}</td>
                                    </tr>
                                ))}

                                <tr className="bg-green-100 font-medium">
                                    <td colSpan="2" className="px-4 py-2 text-sm text-gray-700">TOTAL INWARD</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(inwardTotals.NetAmount)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(inwardTotals.TaxableValue)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(inwardTotals.CGST)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(inwardTotals.SGST)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(inwardTotals.IGST)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(inwardTotals.CESS)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(inwardTotals.TotalTax)}</td>
                                </tr>

                                {/* Spacer */}
                                <tr><td colSpan="9" className="h-2"></td></tr>

                                {/* Outward Supplies */}
                                <tr className="bg-red-50">
                                    <td colSpan="9" className="px-4 py-2 font-medium text-red-800">OUTWARD SUPPLIES (SALES)</td>
                                </tr>

                                {outwardSupplies.map((item, index) => (
                                    <tr key={`outward-${index}`} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{item.Book}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500">{item.Particulars}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.NetAmount)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.TaxableValue)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.CGST)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.SGST)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.IGST)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.CESS)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.TotalTax)}</td>
                                    </tr>
                                ))}

                                <tr className="bg-red-100 font-medium">
                                    <td colSpan="2" className="px-4 py-2 text-sm text-gray-700">TOTAL OUTWARD</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(outwardTotals.NetAmount)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(outwardTotals.TaxableValue)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(outwardTotals.CGST)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(outwardTotals.SGST)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(outwardTotals.IGST)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(outwardTotals.CESS)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(outwardTotals.TotalTax)}</td>
                                </tr>

                                {/* Spacer */}
                                <tr><td colSpan="9" className="h-2"></td></tr>

                                {/* GST Summary */}
                                <tr className="bg-blue-50">
                                    <td colSpan="9" className="px-4 py-2 font-medium text-blue-800">GST SUMMARY</td>
                                </tr>

                                <tr className="bg-blue-100 font-medium">
                                    <td colSpan="7" className="px-4 py-2 text-sm text-gray-700">TOTAL GST CREDIT (INWARD)</td>
                                    <td className="px-4 py-2"></td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(totalGstCredit)}</td>
                                </tr>

                                <tr className="bg-blue-100 font-medium">
                                    <td colSpan="7" className="px-4 py-2 text-sm text-gray-700">TOTAL GST DEBIT (OUTWARD)</td>
                                    <td className="px-4 py-2"></td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(totalGstDebit)}</td>
                                </tr>

                                <tr className="bg-blue-100 font-medium">
                                    <td colSpan="7" className="px-4 py-2 text-sm text-gray-700">DIFF. (TO PAY)</td>
                                    <td className="px-4 py-2"></td>
                                    <td className={`px-4 py-2 text-sm text-right ${diffToPay >= 0 ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                        {formatCurrency(diffToPay)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
        </MainContainer>
    );
}

export default GstPeriodicSummary;